import { Divider, VStack, Text } from '@cad-arc';
import { useApiClient } from '~/api-client';
import { useQuery } from '@tanstack/react-query';
import { intersperse } from '../common/helpers/intersperse';
import { SavedEventSearch } from './saved-event-search';
import { SavedSearches } from '../search-windows/saved-searches';
import { CadSearchsaveResource, EventSearchForm } from '@mark43/cad-api';
import { SavedSearchHandler } from '../fr-page-layout/search-pages/types';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';

export function EventSearchSavedSearches({
    onSavedSearchClick,
    onClose,
}: {
    onSavedSearchClick: SavedSearchHandler<EventSearchForm>;
    onClose?: () => void;
}) {
    const apiClient = useApiClient();
    const { data: eventSearchGetResponse, isInitialLoading, isError, refetch } = useQuery(
        ['saved-event-searches'],
        () => {
            return apiClient
                .request<CadSearchsaveResource.GetEventSearches>({
                    method: 'GET',
                    url: 'cad_search/save/event',
                })
                .then((resp) => resp.data.data);
        }
    );

    const { savedSearchSharingEnabled } = useEnabledFeatures();
    const userSearches =
        eventSearchGetResponse?.savedEventSearchDtos?.filter((s) => !s.isShared) ?? [];
    const sharedSearches =
        eventSearchGetResponse?.savedEventSearchDtos?.filter((s) => s.isShared) ?? [];

    const hasSavedSearches =
        !!eventSearchGetResponse && eventSearchGetResponse.savedEventSearchDtos.length > 0;
    const isNoSavedSearches =
        !!eventSearchGetResponse && savedSearchSharingEnabled
            ? eventSearchGetResponse.savedEventSearchDtos.length === 0
            : userSearches.length === 0;
    const showSharedSearches = sharedSearches.length && savedSearchSharingEnabled;
    return (
        <SavedSearches
            hasSavedSearches={showSharedSearches ? hasSavedSearches : !!userSearches.length}
            isNoSavedSearches={isNoSavedSearches}
            isLoading={isInitialLoading}
            isError={isError}
            onClose={onClose}
            hideTitle={!userSearches?.length}
        >
            <VStack align="left" w="100%">
                {intersperse(
                    userSearches.map((savedSearch) => {
                        return (
                            <SavedEventSearch
                                key={savedSearch.savedSearchId}
                                savedSearch={savedSearch}
                                refetchSavedSearches={refetch}
                                onSavedSearchClick={onSavedSearchClick}
                            />
                        );
                    }) ?? [],
                    <Divider />
                )}
                {showSharedSearches && (
                    <>
                        <Text variant="headingMd">Shared Searches</Text>
                        {intersperse(
                            sharedSearches.map((savedSearch) => {
                                return (
                                    <SavedEventSearch
                                        key={savedSearch.savedSearchId}
                                        savedSearch={savedSearch}
                                        refetchSavedSearches={refetch}
                                        onSavedSearchClick={onSavedSearchClick}
                                    />
                                );
                            }) ?? [],
                            <Divider />
                        )}
                    </>
                )}
            </VStack>
        </SavedSearches>
    );
}
